import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, Minus, Plus, RotateCcw } from 'lucide-react';

import { customerApi } from '../services/customerApi';

/**
 * The five sample views, in the order a person wants to see them: straight down
 * first for orientation, then around the compass.
 */
const ALL_VIEWS = [
  { id: 'nadir',         label: 'Overhead' },
  { id: 'oblique-north', label: 'North' },
  { id: 'oblique-east',  label: 'East' },
  { id: 'oblique-south', label: 'South' },
  { id: 'oblique-west',  label: 'West' },
] as const;

export type PreviewFailure = 'forbidden' | 'expired' | 'service';

interface Props {
  token: string;
  /** Which views the server actually found. A crop may not have every oblique. */
  views?: string[];
  /** Fired once every view has loaded — the point at which we can offer to proceed. */
  onReady: () => void;
  /** Fired on the first failure; 'forbidden' means entitlement, not absence. */
  onFailed: (kind: PreviewFailure) => void;
  compact?: boolean;
}

/**
 * Carousel of the sample imagery, with a full-screen view.
 *
 * Images are fetched with `fetch` rather than handed to an `<img src>` for two
 * reasons: an `<img>` error event carries no status, so a 403 (not entitled)
 * would be indistinguishable from an outage — and that distinction is the whole
 * difference between the two failure messages. Holding the bytes as object URLs
 * also means the thumbnail and the full-screen view are the same fetch, which is
 * what makes browser-side caching the right call here.
 */
const ImageryPreviewPanel: React.FC<Props> = ({ token, views, onReady, onFailed, compact = false }) => {
  /* Ordered by ALL_VIEWS, not by what the server listed, so the carousel always
     runs overhead → N → E → S → W however many of them came back. */
  const PREVIEW_VIEWS = useMemo(
    () => (views ? ALL_VIEWS.filter((v) => views.includes(v.id)) : ALL_VIEWS),
    [views],
  );
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(0);
  /* `go` is stable, so it reads the count through a ref rather than closing
     over a value that was current when it was created. */
  const loadedRef = useRef(0);
  const [failed, setFailed] = useState(false);
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const objectUrls = useRef<string[]>([]);
  // Read through refs so the fetch effect never re-runs when a parent re-renders
  // with new callback identities — re-running it would refetch all five.
  const onReadyRef = useRef(onReady);
  const onFailedRef = useRef(onFailed);
  useEffect(() => { onReadyRef.current = onReady; onFailedRef.current = onFailed; });

  useEffect(() => {
    let cancelled = false;
    /*
     * A new grant is a new set of pictures, so everything the old one left goes
     * with it. Only `loadedRef` was reset before, which left four things behind:
     * `loaded` counting on from the previous total ("7 of 5"); `urls` merging
     * onto a map whose blob: URLs the cleanup below had just revoked, so the
     * carousel showed dead images until each replacement arrived; `failed`
     * latched, so a panel that failed once rendered nothing ever again however
     * good the new grant; and `index` left wherever the reader had it — past the
     * end, when the new grant carries fewer views, where `PREVIEW_VIEWS[index]`
     * is undefined and reading `.id` off it throws.
     */
    loadedRef.current = 0;
    setUrls({});
    setLoaded(0);
    setFailed(false);
    setIndex(0);
    setExpanded(false);

    (async () => {
      for (const view of PREVIEW_VIEWS) {
        if (cancelled) return;
        let res: Response;
        try {
          res = await fetch(customerApi.worldMapImageUrl(token, view.id));
        } catch {
          if (!cancelled) { setFailed(true); onFailedRef.current('service'); }
          return;
        }
        if (!res.ok) {
          if (!cancelled) {
            setFailed(true);
            /*
             * 410 is the proxy saying the grant aged out — the imagery is fine,
             * the permission to fetch it is not. Reporting that as a service
             * problem sends someone to check on an outage that is not happening;
             * it is a re-check, not a retry.
             */
            onFailedRef.current(
              res.status === 403 ? 'forbidden' : res.status === 410 ? 'expired' : 'service',
            );
          }
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        objectUrls.current.push(url);
        setUrls((prev) => ({ ...prev, [view.id]: url }));
        setLoaded((n) => { loadedRef.current = n + 1; return n + 1; });
      }
      if (!cancelled) onReadyRef.current();
    })();

    return () => {
      cancelled = true;
      objectUrls.current.forEach(URL.revokeObjectURL);
      objectUrls.current = [];
    };
  }, [token, PREVIEW_VIEWS]);

  /*
   * Wraps. The views are a compass — west's neighbour is north — so stopping at
   * the ends makes the reader back all the way out to see the one they passed.
   * Only meaningful past the first image: the modulo of one is always itself.
   */
  const go = useCallback((delta: number) => {
    setIndex((i) => {
      const n = loadedRef.current;
      if (n < 2) return 0;
      return (i + delta + n) % n;
    });
  }, []);

  /*
   * Zoom, expanded only.
   *
   * The inline frame is 176-240px tall and sits in a scrolling transcript;
   * capturing the wheel there would steal the page scroll to magnify a
   * thumbnail. Full screen is where someone is actually inspecting a roof.
   */
  const MIN_SCALE = 1;
  const MAX_SCALE = 6;
  /*
   * Scale and pan are ONE piece of state, and every update is a pure function of
   * it. They were two, with setOffset called from inside the setScale updater —
   * which React is free to invoke more than once, and StrictMode deliberately
   * does. The offset then compounded by k squared while the scale advanced by k,
   * so the image drifted off-screen after a few steps and zoom appeared to stop
   * at an arbitrary factor: 1.7x, 2.3x, 6x, depending only on how far from
   * centre the cursor happened to be.
   */
  interface View { scale: number; x: number; y: number }
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const { scale } = view;
  const frameRef = useRef<HTMLDivElement>(null);
  /** Active pointers, so one finger pans and two pinch. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null);
  const panRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  /** Keep the magnified image overlapping the frame — panning cannot lose it. */
  const clampPan = (x: number, y: number, s: number): { x: number; y: number } => {
    const el = frameRef.current;
    if (!el || s <= MIN_SCALE) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    const mx = (r.width * (s - 1)) / 2;
    const my = (r.height * (s - 1)) / 2;
    return { x: Math.max(-mx, Math.min(mx, x)), y: Math.max(-my, Math.min(my, y)) };
  };

  const resetZoom = useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);

  // A new view, or leaving full screen, starts fresh — carrying a pan across
  // would drop the reader somewhere arbitrary in a different photograph.
  useEffect(() => { resetZoom(); }, [index, expanded, resetZoom]);

  /**
   * Zoom to an absolute factor about a point, so whatever is under the cursor
   * stays under it. Absolute rather than relative because the pinch handler
   * knows the target scale outright and reading the current one from a closure
   * is how a gesture handler goes stale mid-drag.
   */
  const zoomTo = useCallback((target: number, cx?: number, cy?: number) => {
    setView((v) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, target));
      if (next === v.scale) return v;
      if (next === MIN_SCALE) return { scale: next, x: 0, y: 0 };
      const el = frameRef.current;
      if (!el || cx === undefined || cy === undefined) return { ...v, scale: next };
      const r = el.getBoundingClientRect();
      const px = cx - r.left - r.width / 2;
      const py = cy - r.top - r.height / 2;
      const k = next / v.scale;
      const p = clampPan(px - (px - v.x) * k, py - (py - v.y) * k, next);
      return { scale: next, x: p.x, y: p.y };
    });
  }, []);

  /** Relative step, for the wheel, the buttons and the keys. */
  const zoomAt = useCallback((factor: number, cx?: number, cy?: number) => {
    setView((v) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
      if (next === v.scale) return v;
      if (next === MIN_SCALE) return { scale: next, x: 0, y: 0 };
      const el = frameRef.current;
      if (!el || cx === undefined || cy === undefined) return { ...v, scale: next };
      const r = el.getBoundingClientRect();
      const px = cx - r.left - r.width / 2;
      const py = cy - r.top - r.height / 2;
      const k = next / v.scale;
      const p = clampPan(px - (px - v.x) * k, py - (py - v.y) * k, next);
      return { scale: next, x: p.x, y: p.y };
    });
  }, []);

  const panBy = useCallback((dx: number, dy: number) => {
    setView((v) => {
      const p = clampPan(v.x + dx, v.y + dy, v.scale);
      return { ...v, x: p.x, y: p.y };
    });
  }, []);

  const panTo = useCallback((x: number, y: number) => {
    setView((v) => {
      const p = clampPan(x, y, v.scale);
      return { ...v, x: p.x, y: p.y };
    });
  }, []);

  useEffect(() => {
    const el = frameRef.current;
    if (!el || !expanded) return;
    // Non-passive: the browser would otherwise scroll the page behind us.
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [expanded, zoomAt]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!expanded) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchRef.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale };
      panRef.current = null;
    } else if (scale > 1) {
      panRef.current = { x: e.clientX, y: e.clientY, ox: view.x, oy: view.y };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const target = pinchRef.current.scale * (dist / pinchRef.current.dist);
      zoomTo(target, (a.x + b.x) / 2, (a.y + b.y) / 2);
      return;
    }
    if (panRef.current) {
      panTo(
        panRef.current.ox + (e.clientX - panRef.current.x),
        panRef.current.oy + (e.clientY - panRef.current.y),
      );
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;
    if (pointers.current.size === 0) panRef.current = null;
  };

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (scale > 1) { resetZoom(); return; } setExpanded(false); }
      if (e.key === '+' || e.key === '=') zoomAt(1.3);
      if (e.key === '-' || e.key === '_') zoomAt(1 / 1.3);
      if (e.key === '0') resetZoom();
      // Panning owns the arrows once zoomed; otherwise they change view.
      if (e.key === 'ArrowRight') {
        if (scale > 1) panBy(-60, 0); else go(1);
      }
      if (e.key === 'ArrowLeft') {
        if (scale > 1) panBy(60, 0); else go(-1);
      }
      if (e.key === 'ArrowUp'   && scale > 1) panBy(0, 60);
      if (e.key === 'ArrowDown' && scale > 1) panBy(0, -60);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [expanded, go, scale, zoomAt, resetZoom, panBy]);

  // Never index past the end: a crash here would take the whole chat down, and
  // showing the first view is a better failure than a blank screen.
  const current = PREVIEW_VIEWS[index] ?? PREVIEW_VIEWS[0];
  const currentUrl = urls[current.id];
  const total = PREVIEW_VIEWS.length;

  if (failed) return null;

  const zoomed = expanded && scale > 1;

  const frame = (
    <div
      ref={frameRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      /*
       * Double-click zooms the picture — but only the picture. The controls stop
       * `pointerdown` from reaching the pan handler, which is not the same thing
       * as stopping `dblclick`: two quick presses of + are an ordinary way to
       * zoom in, and the second one used to arrive here as a double-click and
       * throw the zoom away. It looked random because it depended purely on how
       * fast the two clicks were, which is invisible. Screening on the target
       * covers the zoom pair, Reset and the carousel arrows at once, and keeps
       * covering anything added to the bar later.
       */
      onDoubleClick={(e) => {
        if ((e.target as Element).closest('button')) return;
        if (scale > 1) resetZoom(); else zoomAt(2.5, e.clientX, e.clientY);
      }}
      className={expanded
        ? `relative flex min-h-0 flex-1 items-center justify-center overflow-hidden ${
            zoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
          }`
        : `relative w-full overflow-hidden rounded-lg border border-crane-500/30 bg-ink-900 ${compact ? 'h-44' : 'h-60'}`}
      // A pinch must reach our handler rather than being eaten as a page gesture.
      style={expanded ? { touchAction: 'none' } : undefined}
    >
      {currentUrl ? (
        <img
          src={currentUrl}
          alt={`${current.label} view of the selected area`}
          draggable={false}
          className={expanded ? 'max-h-full max-w-full select-none object-contain' : 'h-full w-full object-cover'}
          style={expanded ? {
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            // Animate the button and keyboard steps, but never a drag or a
            // wheel — a transition there lags the pointer.
            transition: panRef.current || pinchRef.current ? 'none' : 'transform 120ms ease-out',
            willChange: 'transform',
          } : undefined}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-meta text-ink-400">Loading {current.label.toLowerCase()}…</span>
        </div>
      )}

      {/* Only offered once there is something on either side to reach. Expanded,
          the frame is the whole window but the image is letterboxed inside it,
          so the arrows track the image rather than the window edges — otherwise
          they sit in far corners with nothing near them.
          They stay put while zoomed; stopPropagation on pointerdown is what
          keeps a press on one from also starting a pan underneath it. */}
      {loaded > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Previous view"
            className={`absolute top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-black/40 bg-ink-950/80 text-ink-100 backdrop-blur-sm transition-colors hover:bg-ink-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500 ${
              expanded ? 'left-[max(0.75rem,calc(50%-30rem))]' : 'left-2'
            }`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Next view"
            className={`absolute top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-black/40 bg-ink-950/80 text-ink-100 backdrop-blur-sm transition-colors hover:bg-ink-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500 ${
              expanded ? 'right-[max(0.75rem,calc(50%-30rem))]' : 'right-2'
            }`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}

      {/*
        * One bar, centred, at the top. Pinned to opposite corners these drifted
        * to the far edges of a wide monitor while the image sat letterboxed in
        * the middle, so the label naming what you were looking at was nowhere
        * near it. Top rather than bottom: the pan gesture and the reader's
        * attention both live in the lower half of a photograph.
        *
        * Reset is deliberately separated from the +/- pair by a divider and a
        * gap, and carries a word rather than an icon. Sitting flush against +
        * as a same-sized glyph, it was being hit by people reaching to zoom in
        * — and unlike a mis-hit on +, that one throws away their position.
        */}
      <div className="pointer-events-none absolute inset-x-0 top-2 flex justify-center px-2">
        <div className="pointer-events-auto flex items-center gap-1.5 rounded-lg border border-black/40 bg-ink-950/85 p-1.5 backdrop-blur-sm">
          <span className="px-1.5 text-meta font-medium text-ink-100">{current.label}</span>

      {expanded && currentUrl && (
        <>
          <span aria-hidden className="h-6 w-px bg-ink-700" />
          <button type="button" onPointerDown={(e) => e.stopPropagation()}
            onClick={() => zoomAt(1 / 1.3)} disabled={scale <= MIN_SCALE}
            aria-label="Zoom out"
            className="flex h-9 w-9 items-center justify-center rounded-md text-ink-100 transition-colors hover:bg-ink-800 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500">
            <Minus className="h-4 w-4" />
          </button>
          <span className="w-11 text-center text-meta tabular-nums text-ink-300">{scale.toFixed(1)}×</span>
          <button type="button" onPointerDown={(e) => e.stopPropagation()}
            onClick={() => zoomAt(1.3)} disabled={scale >= MAX_SCALE}
            aria-label="Zoom in"
            className="flex h-9 w-9 items-center justify-center rounded-md text-ink-100 transition-colors hover:bg-ink-800 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500">
            <Plus className="h-4 w-4" />
          </button>
          <span aria-hidden className="ml-1 h-6 w-px bg-ink-700" />
          <button type="button" onPointerDown={(e) => e.stopPropagation()}
            onClick={resetZoom} disabled={scale === MIN_SCALE}
            className="ml-1 flex h-9 items-center gap-1.5 rounded-md px-2.5 text-meta font-medium text-ink-300 transition-colors hover:bg-ink-800 hover:text-ink-100 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500">
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </button>
        </>
      )}
        </div>
      </div>
    </div>
  );

  const body = (
    <div className={expanded ? 'flex h-full min-h-0 flex-col gap-2' : 'flex flex-col gap-2'}>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-ui text-ink-200">
          {loaded < total ? `Loading sample imagery… ${loaded} of ${total}` : `${loaded} sample views`}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Shrink images' : 'Expand images'}
          className={`ml-auto flex w-fit flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-ui font-medium transition-colors focus:outline-none focus-visible:ring-2 ${
            expanded
              ? 'bg-crane-500 text-ink-950 hover:bg-crane-600 focus-visible:ring-crane-400'
              : 'border border-ink-600 bg-ink-850 text-ink-200 hover:border-crane-500/60 hover:bg-ink-800 hover:text-ink-100 focus-visible:ring-crane-500'
          }`}
        >
          {expanded
            ? <><Minimize2 className="h-3.5 w-3.5" /> Shrink</>
            : <><Maximize2 className="h-3.5 w-3.5 text-crane-500" /> Expand</>}
        </button>
      </div>

      {frame}

      {/* Dots rather than thumbnails: five extra <img> elements would each be a
          second decode of an image already on screen. */}
      <div className="flex items-center justify-center gap-1.5">
        {PREVIEW_VIEWS.map((v, i) => (
          <button
            key={v.id}
            type="button"
            onClick={() => setIndex(i)}
            disabled={!urls[v.id]}
            aria-label={v.label}
            aria-current={i === index}
            className={`h-1.5 rounded-full transition-all disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-500 ${
              i === index ? 'w-5 bg-crane-500' : 'w-1.5 bg-ink-600 hover:bg-ink-500'
            }`}
          />
        ))}
      </div>
    </div>
  );

  /*
   * Expanded is a fixed overlay in place, not a portal: moving the node would
   * remount the <img> elements and drop the decoded bitmaps. Same reasoning as
   * the map control, for the same reason.
   */
  return (
    <div>
      <div
        role={expanded ? 'dialog' : undefined}
        aria-modal={expanded || undefined}
        aria-label={expanded ? 'Sample imagery' : undefined}
        className={expanded ? 'fixed inset-0 z-[100] flex flex-col bg-ink-950 p-3 pb-6 sm:p-5 sm:pb-8' : undefined}
      >
        {body}
      </div>
    </div>
  );
};

export default ImageryPreviewPanel;
